import { getOrders } from '../common/api/orders';
import { getOrdersMeta } from '../common/api/orders';
import { Order, VenueFinancialReportRead } from '../common/types';
import { getDuration } from '../common/orders';
import { RentalStatusIcon } from '../common/orders';
import { getDurationString } from '../common/filtering/filter';

import { DefinedQueryTableProps, QueryTable } from './QueryTable';
import { DeviceLink } from './DeviceLink';
import { VenueLink } from './VenueLink';
import {
  Box,
  Button,
  CollectionPreferences,
  Header,
  Pagination,
  SpaceBetween,
  Table,
  TableProps,
} from '@cloudscape-design/components';
import { ResourceError } from './Loading';
import { useEffect, useMemo, useState } from 'react';
import { useCollection } from '@cloudscape-design/collection-hooks';

export type VenueFinancialReportTableProps = {
  data?: VenueFinancialReportRead[];
  hideActions?: boolean;
  hideFilter?: boolean;
  hideDateRange?: boolean;
  hidePagination?: boolean;
  hiddenColumns?: string[];
  variant?: TableProps.Variant;
  additionalActions?: React.ReactNode;
  description?: string;
  loading?: boolean;
  error?: Error | null;
  pageSize?: number;
  page: number;
  setPage: (page: number) => void;
};

export function VenueFinancialReportTable(props: VenueFinancialReportTableProps) {
  const pageSize = props.pageSize ?? 15;
  const page = props.page;
  const setPage = props.setPage;

  const data = useMemo(() => {
    const sortedData = [...(props.data ?? [])];
    sortedData.sort((a, b) => b.revenue_ytd - a.revenue_ytd);

    return sortedData;
  }, [props.data]);

  const { items: allItems, collectionProps } = useCollection(data, {
    sorting: {
      defaultState: {
        sortingColumn: {
          sortingField: 'revenue_ytd',
          sortingComparator(a, b) {
            const x = a.revenue_ytd;
            const y = b.revenue_ytd;

            return x < y ? -1 : x > y ? 1 : 0;
          },
        },
        isDescending: true,
      },
    },
  });

  const items = useMemo(() => {
    return allItems.slice((page - 1) * pageSize, page * pageSize);
  }, [allItems, page, pageSize]);

  const pageCount = useMemo(() => {
    return Math.ceil(allItems.length / pageSize);
  }, [allItems, pageSize]);

  const pagination = (
    <Pagination
      currentPageIndex={page}
      disabled={props.loading}
      pagesCount={pageCount}
      onChange={({ detail }) => {
        setPage(detail.currentPageIndex);
      }}
    />
  );

  return (
    <Table
      {...collectionProps}
      empty={
        <Box color="inherit" margin={{ vertical: 'xs' }} textAlign="center">
          {props.error ? (
            <ResourceError error={props.error} resourceName="Venue Financials" />
          ) : (
            <SpaceBetween size="xxs">
              <div>
                <b>No venue reports</b>
                <Box color="inherit" variant="p">
                  No venue reports found.
                </Box>
              </div>
            </SpaceBetween>
          )}
        </Box>
      }
      pagination={pagination}
      header={
        <Header
          actions={
            props.hideActions ? undefined : (
              <SpaceBetween direction="horizontal" size="s">
                {props.additionalActions}
              </SpaceBetween>
            )
          }
          counter={data.length ? `(${data.length})` : undefined}
          description={props.description}
          variant={
            !props.variant
              ? 'awsui-h1-sticky'
              : props.variant !== 'full-page'
              ? 'h2'
              : 'awsui-h1-sticky'
          }
        >
          Venues
        </Header>
      }
      items={items}
      loading={props.loading}
      loadingText="Loading venue reports..."
      // pagination={props.hidePagination ? undefined : pagination}
      // preferences={
      //   <CollectionPreferences
      //     disabled={loading}
      //     pageSizePreference={{
      //       options: PAGE_SIZE_OPTIONS,
      //     }}
      //     preferences={preferences}
      //     onConfirm={(e) => setPreferences(e.detail)}
      //   />
      // }
      stickyHeader={!props.variant || props.variant === 'full-page'}
      variant={props.variant ?? 'full-page'}
      columnDefinitions={[
        {
          id: 'venue',
          header: 'Venue',
          cell: (e: VenueFinancialReportRead) => (
            <VenueLink venue={{ name: e.name, uuid: e.uuid }} />
          ),
          sortingField: 'name',
          sortingComparator(a: VenueFinancialReportRead, b: VenueFinancialReportRead) {
            return a.name.localeCompare(b.name);
          },
        },
        {
          id: 'revenue-wtd',
          sortingField: 'revenue_wtd',
          sortingComparator(a: VenueFinancialReportRead, b: VenueFinancialReportRead) {
            return a.revenue_wtd - b.revenue_wtd;
          },
          header: 'Revenue WTD',
          cell: (e: VenueFinancialReportRead) =>
            `$${e.revenue_wtd.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} ${e.currency}`,
        },
        {
          id: 'revenue-mtd',
          sortingField: 'revenue_mtd',
          sortingComparator(a: VenueFinancialReportRead, b: VenueFinancialReportRead) {
            return a.revenue_mtd - b.revenue_mtd;
          },
          header: 'Revenue MTD',
          cell: (e: VenueFinancialReportRead) =>
            `$${e.revenue_mtd.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} ${e.currency}`,
        },
        {
          id: 'revenue-ytd',
          sortingField: 'revenue_ytd',
          sortingComparator(a: VenueFinancialReportRead, b: VenueFinancialReportRead) {
            return a.revenue_ytd - b.revenue_ytd;
          },
          header: 'Revenue YTD',
          cell: (e: VenueFinancialReportRead) =>
            `$${e.revenue_ytd.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} ${e.currency}`,
        },
        {
          id: 'offline-machines',
          sortingField: 'offline_machines',
          sortingComparator(a: VenueFinancialReportRead, b: VenueFinancialReportRead) {
            return a.offline_machines - b.offline_machines;
          },
          header: '# Offline Machines',
          cell: (e: VenueFinancialReportRead) => e.offline_machines.toLocaleString(),
        },
      ].filter((c) => !c.id || !props.hiddenColumns?.includes(c.id))}
      trackBy="uuid"
    />
  );
}
